<template>
  <!-- App.vue -->

  <v-app>
    <v-app-bar app color="white" flat class="border-b">
      <v-app-bar-nav-icon v-if="!isDesktop" @click.stop="drawer = !drawer" data-test="nav-menu-btn" aria-label="Open navigation menu"></v-app-bar-nav-icon>

      <a href="/" class="d-flex align-center ml-4">
        <AppLogoSquare v-if="!isDesktop" height="40" width="40" />
        <AppLogoLandscape v-else height="40" width="120" />
      </a>

      <v-spacer />

      <get-help-button :url="appStore.getHelpUrl"></get-help-button>
      <LoginButton :user="userStore.user" @login="showLoginBox" @logout="logoutUser"></LoginButton>
      <TheUserMenu
        v-if="showUserMenu"
        :username="loggedInUsername"
        @LOGOUT_REQUEST="logout"
      />
      <language-menu />
    </v-app-bar>

    <AppSidebar v-model="drawer" :permanent="isDesktop" />

    <GlobalDialog />
    <ErrorDialog />
    <notification-snackbar-wrapper />
    <transition name="fade">
      <div>
        <LoadingSpinner
          :get-loading-color="getLoadingColor"
          :loading-state="loadingState.isLoading.value"
        />
      </div>
    </transition>
    <v-main>
      <!-- Provides the application the proper gutter -->
      <v-container class="main-content px-4 px-lg-6 px-xl-8">
        <div class="page-header mb-6 mt-2">
          <v-breadcrumbs :items="breadcrumbs" class="pa-0 pb-2 page-breadcrumbs">
            <template v-slot:divider>
              <v-icon size="18">mdi-chevron-right</v-icon>
            </template>
          </v-breadcrumbs>
          <h1 class="page-title text-h4 font-weight-bold mt-0 mb-4">{{ pageTitle.plainTitle }}</h1>
        </div>
        <template v-if="route.matched.length && route.meta.error">
          <PageNotFound
            v-if="route.matched.length && route.meta.errorCode == 404"
          />
          <NotAuthorised v-else-if="route.matched.length && route.meta.error" />
        </template>
        <template v-else-if="route.matched.length">
          <router-view></router-view>
        </template>
        <!-- If using vue-router -->
        <the-login-dialog />
      </v-container>
      <usersnap v-if="isUsersnap" />
      <app-footer />
    </v-main>
  </v-app>
</template>

<script lang="ts">
import {computed, defineComponent, provide, ref, watch, onMounted, onUnmounted} from 'vue';
import AppFooter from '@/components/layout/AppFooter.vue';
import TheLoginDialog from '@/features/authentication/components/TheLoginDialog.vue';
import NotificationSnackbarWrapper from './features/notifications/NotificationSnackbarWrapper.vue';
import { AppServiceSymbol, getAppService } from '@/statemachines/app.machine';
import { getSearchService, searchSymbol } from '@/statemachines/search.machine';
import Usersnap from '@/components/Usersnap.vue';
import NotAuthorised from './pages/NotAuthorised.vue';
import { env } from '@/env';
import { useRoute } from 'vue-router';
import PageNotFound from './pages/PageNotFound.vue';
import { store } from '@/statemachines/store';
import { URL_LOGO_HORIZONTAL_RECTANGLE } from './constants/urls';
import { useLoadingColors } from '@/composables/useLoadingColors';
import { useLoadingWithPromises } from './composables/useLoadingWithPromises';
import GlobalDialog from '@/components/dialogs/GlobalDialog.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import useTheme, { ThemeName } from '@/composables/useTheme';
import { useTheme as useThemeVuetify, useDisplay } from 'vuetify';
import '@/styles/main.scss'
import ErrorDialog from '@/components/dialogs/error/ErrorDialog.vue';
import AppLogoLandscape from '@/components/branding/AppLogoLandscape.vue';
import GetHelpButton from "@/components/common/GetHelpButton.vue";
import LoginButton from '@/components/common/LoginButton.vue';
import TheUserMenu from '@/components/user/TheUserMenu.vue';
import LanguageMenu from '@/components/user/TheLanguageMenu.vue';
import { useAppStore } from "@/stores/app";
import { useUserStore } from '@/stores/user';
import { useAuth, useUserInfo } from '@/composables/useAuth';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { useMenuItems } from '@/composables/useMenuItems';
import AppSidebar from '@/components/layout/AppSidebar.vue';
import { usePageTitle } from '@/composables/usePageTitle';
import { mapStores } from 'pinia';
import AppLogoSquare from '@/components/branding/AppLogoSquare.vue';

export default defineComponent({
  name: 'App',
  components: {
    ErrorDialog,
    LoadingSpinner,
    AppFooter,
    TheLoginDialog,
    NotificationSnackbarWrapper,
    Usersnap,
    NotAuthorised,
    PageNotFound,
    GlobalDialog,
    AppLogoLandscape,
    GetHelpButton,
    LoginButton,
    TheUserMenu,
    LanguageMenu,
    AppSidebar,
    AppLogoSquare,
  },
  setup() {
    const loadingState = useLoadingWithPromises();
    const loading = useLoadingColors();
    const getLoadingColor = computed(() => {
      return loading.color.value;
    });
    const route = useRoute();
    const searchService = getSearchService();
    provide(searchSymbol, searchService);
    const appService = getAppService();
    provide(AppServiceSymbol, appService);
    const headerRef = ref(null);
    provide('headerRef', headerRef);

    const appStore = useAppStore();
    const userStore = useUserStore();
    const { logout } = useAuth();
    const { id: loggedInUserId, mail: loggedInUserEmail } = useUserInfo();
    const showLoginBox = () => (appService as any).send('SHOW_LOGIN_BOX');
    const logoutUser = () => userStore.logoutUser();
    const loggedInUsername = computed(() => loggedInUserEmail.value ?? loggedInUserId.value);
    const showUserMenu = computed(() => env().isKeycloakEnabled);
    const { breadcrumbs } = useBreadcrumbs();
    const { menuItems } = useMenuItems();
    const pageTitle = usePageTitle();
    const display = useDisplay();
    const isDesktop = computed(() => display.lgAndUp.value);
    const drawer = ref(isDesktop.value);

    watch(
      () => route.meta,
      (newMeta) => {
        const titleFromMeta = newMeta.title;
        if (typeof titleFromMeta === 'string') {
          pageTitle.setTitle(titleFromMeta);
        } else if (typeof titleFromMeta === 'function') {
          // @ts-ignore
          pageTitle.setTitle(titleFromMeta(route));
        }
      },
      { immediate: true }
    );

    /**
     * useTheme of Vuetify can only be used within a component which is why we setup the theme handling here
     */
    const { currentTheme, themes } = useTheme();
    const vuetifyTheme = useThemeVuetify();
    watch(currentTheme, (newTheme: ThemeName) => {
      if (themes.value[newTheme]) {
        vuetifyTheme.global.name.value = newTheme;
      }
    });

    return {
      route,
      store,
      URL_LOGO_HORIZONTAL_RECTANGLE,
      getLoadingColor,
      loadingState,
      currentTheme,
      headerRef,
      appStore,
      userStore,
      logout,
      showLoginBox,
      logoutUser,
      loggedInUsername,
      showUserMenu,
      breadcrumbs,
      menuItems,
      pageTitle,
      isDesktop,
      drawer,
    };
  },
  computed: {
    ...mapStores(useAppStore, useUserStore),
    isUsersnap: () => env().isUsersnap,
  },
  watch: {
    $route(to, from) {
      console.debug('$route changed', to, from);
    },
  },
});
</script>

<style>
@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.page-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 0.5rem;
  margin-bottom: 2rem;
}
.page-breadcrumbs {
  font-size: 1rem;
  color: #757575;
}
.page-title {
  color: #222;
  letter-spacing: -0.5px;
}
.main-content {
  padding-bottom: 80px; /* Height of the footer + a little extra */
}
</style>
